/**
 * Proof of Grind – kleurvarianten.
 * 1. Productpagina (<pog-kleur-kiezer>): klik op een bolletje → doelvariant berekenen uit de NU aangevinkte
 *    optieknoppen (JSON per kleur: "optiewaarden|…" → variant-id), navigeren zonder herladen (pog-navigate),
 *    hoofdfoto van de doelkleur vooraf laden bij pointerenter/pointerdown/focus. Label toont bij hover de kleurnaam.
 * 2. Productkaart (.pog-kleur--card): hover (desktop) / tik (mobiel) op bolletje wisselt foto's, links en prijs
 *    van de kaart. Event delegation op document: kaarten worden bij filterwissel opnieuw gerenderd.
 */
import { navigateMorph, prefetchImage } from '@pog/navigate';

/* ---------- helpers ---------- */

function readJson(el) {
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    return null;
  }
}

/** Breedte die de srcset van de galerij-hoofdfoto kiest (zelfde lijst als pog-gallery-prefetch.js). */
function galleryWidth() {
  const img = document.querySelector('.pog-gallery .pog-gallery__img');
  const cssWidth = img ? img.getBoundingClientRect().width : window.innerWidth;
  const need = cssWidth * (window.devicePixelRatio || 1);
  const widths = [400, 600, 800, 1000, 1200, 1500];
  return widths.find((w) => w >= need) || widths[widths.length - 1];
}

/* ---------- productpagina ---------- */

function pickerAt(kiezer) {
  const section = kiezer.closest('.shopify-section') || document;
  return section.querySelector('variant-picker') || document.querySelector('variant-picker');
}

/** Optie-1-waarden van het huidige product in volgorde (voor de match op positie). */
function currentOpt1(kiezer) {
  const picker = pickerAt(kiezer);
  const fs = picker && picker.querySelector('fieldset');
  if (!fs) return [];
  return [...fs.querySelectorAll('input')].map((i) => i.value);
}

function checkedValuesAt(kiezer) {
  const picker = pickerAt(kiezer);
  if (!picker) return [];
  return [...picker.querySelectorAll('fieldset')].map((fs) => {
    // property eerst; valt terug op het attribuut als een morph de property niet heeft gezet
    const c =
      fs.querySelector('input:checked') ||
      fs.querySelector('input[data-current-checked="true"]') ||
      fs.querySelector('input[checked]');
    return c ? c.value : null;
  });
}

/** Doel-URL + hoofdfoto-url voor een bolletje/richtingknop, op basis van de nu aangevinkte opties.
 *  Formaat (optie 1) op POSITIE: index van de huidige waarde in de huidige optielijst → zelfde index in het doel
 *  (zo wordt 30×40 ↔ 40×30). Lijst (optie 2) op naam. */
function kiezerTarget(kiezer, item) {
  const el = kiezer.querySelector('script[data-pog-kleuren]');
  const data = el ? readJson(el) : null;
  const entry = data && item.dataset.productId && data[item.dataset.productId];
  const fallback = item.dataset.href || item.getAttribute('href') || '';
  if (!entry) return { href: fallback, mediaUrl: null };
  const values = checkedValuesAt(kiezer);
  const isPoster = kiezer.dataset.type === 'poster';
  const url = isPoster ? entry.posterUrl : entry.url;
  const map = (isPoster ? entry.posterVariants : entry.variants) || {};
  const media = (isPoster ? entry.posterMedia : entry.media) || {};
  const targetOpt1 = (isPoster ? entry.posterOpt1 : entry.opt1) || [];
  if (!url || !values.length || values.includes(null)) return { href: fallback, mediaUrl: null };

  const cur1 = currentOpt1(kiezer);
  const pos = cur1.indexOf(values[0]);
  const want1 = pos > -1 && targetOpt1[pos] ? targetOpt1[pos] : values[0];
  const wanted = [want1, ...values.slice(1)];

  let key = wanted.join('|');
  let id = map[key];
  if (!id) {
    // alleen optie 1 matchen (poster, of lijstnaam bestaat niet in het doel)
    const k = Object.keys(map).find((x) => x.split('|')[0] === want1);
    if (k) {
      id = map[k];
      key = k;
    }
  }
  if (!id) return { href: fallback, mediaUrl: null };
  const dest = new URL(url, window.location.origin);
  dest.searchParams.set('variant', String(id));
  return { href: dest.toString(), mediaUrl: media[key] || null };
}

/** Hrefs van alle bolletjes bijwerken (na elke variantwissel), zodat ctrl-klik/nieuw tabblad klopt. */
function refreshKiezerLinks() {
  document.querySelectorAll('pog-kleur-kiezer').forEach((kiezer) => {
    kiezer.querySelectorAll('.pog-kleur__item, .pog-richting__item').forEach((item) => {
      if (item.getAttribute('aria-disabled') === 'true') return;
      const t = kiezerTarget(kiezer, item);
      if (t.href) item.setAttribute('href', t.href);
    });
  });
}

function activeNaam(kiezer) {
  const a = kiezer.querySelector('.pog-kleur__item[aria-current="true"]');
  return a ? a.dataset.naam : '';
}

function kiezerItem(event) {
  const t = event.target instanceof Element ? event.target : null;
  const item = t && t.closest('pog-kleur-kiezer .pog-kleur__item, pog-kleur-kiezer .pog-richting__item');
  if (!item) return null;
  return { item, kiezer: item.closest('pog-kleur-kiezer') };
}

// Delegation op document (capture): overleeft vervangen van <main> en Horizon's section-morphs.
document.addEventListener(
  'click',
  (event) => {
    const hit = kiezerItem(event);
    if (!hit) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
    event.preventDefault();
    event.stopPropagation();
    const { item, kiezer } = hit;
    if (item.getAttribute('aria-current') === 'true' || item.getAttribute('aria-disabled') === 'true') return;
    const { href } = kiezerTarget(kiezer, item);
    if (!href) return;
    item.setAttribute('aria-busy', 'true');
    navigateMorph(href);
  },
  true
);

const intent = (event) => {
  const hit = kiezerItem(event);
  if (!hit) return;
  const { item, kiezer } = hit;
  if (item.getAttribute('aria-current') === 'true' || item.getAttribute('aria-disabled') === 'true') return;
  const { mediaUrl } = kiezerTarget(kiezer, item);
  if (mediaUrl) prefetchImage(mediaUrl.replace(/width=\d+/, 'width=' + galleryWidth()));
  const naam = kiezer.querySelector('[ref="naam"]');
  if (naam && item.dataset.naam) naam.textContent = item.dataset.naam;
};
document.addEventListener('pointerenter', intent, { capture: true, passive: true });
document.addEventListener('pointerdown', intent, { capture: true, passive: true });
document.addEventListener('focusin', intent, true);

const intentEnd = (event) => {
  const hit = kiezerItem(event);
  if (!hit) return;
  if (!hit.item.classList.contains('pog-kleur__item')) return;
  const related = event.relatedTarget instanceof Element ? event.relatedTarget.closest('.pog-kleur__item') : null;
  if (related) return;
  const naam = hit.kiezer.querySelector('[ref="naam"]');
  if (naam) naam.textContent = activeNaam(hit.kiezer);
};
document.addEventListener('pointerleave', intentEnd, { capture: true, passive: true });
document.addEventListener('focusout', intentEnd, true);

// Hrefs bijwerken na een variantwissel (direct bij change, en nogmaals als Horizon klaar is met morphen)
document.addEventListener('change', (event) => {
  if (event.target instanceof Element && event.target.closest('variant-picker')) {
    refreshKiezerLinks();
    setTimeout(refreshKiezerLinks, 400);
  }
});
document.addEventListener('shopify:product:select', (event) => {
  const p = event.promise;
  if (p && typeof p.then === 'function') p.then(() => requestAnimationFrame(refreshKiezerLinks)).catch(() => {});
});
document.addEventListener('pog:navigated', () => requestAnimationFrame(refreshKiezerLinks));
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', refreshKiezerLinks, { once: true });
} else {
  refreshKiezerLinks();
}

if (!customElements.get('pog-kleur-kiezer')) {
  customElements.define('pog-kleur-kiezer', class extends HTMLElement {});
}

/* ---------- productkaart ---------- */

function cardOf(item) {
  return item.closest('product-card') || item.closest('.product-card') || item.closest('.resource-card');
}

function selectCardColor(item) {
  const card = cardOf(item);
  if (!card || item.getAttribute('aria-disabled') === 'true') return;
  const list = item.closest('.pog-kleur');
  list?.querySelectorAll('.pog-kleur__item').forEach((a) => {
    if (a === item) a.setAttribute('aria-current', 'true');
    else a.removeAttribute('aria-current');
  });

  // foto's
  const frame = card.querySelector('.pog-card-gallery__frame');
  const tpl = item.querySelector('template[data-pog-kleur-media]');
  if (frame && tpl) {
    const imgs = [...tpl.content.querySelectorAll('img')];
    const main = imgs.find((i) => i.classList.contains('pog-card-gallery__img--main'));
    const hover = imgs.find((i) => i.classList.contains('pog-card-gallery__img--hover'));
    const mainBox = frame.querySelector('.pog-card-gallery__media:not(.pog-card-gallery__media--hover)');
    const hoverBox = frame.querySelector('.pog-card-gallery__media--hover');
    if (main && mainBox) mainBox.replaceChildren(main.cloneNode(true));
    if (hoverBox) {
      if (hover) hoverBox.replaceChildren(hover.cloneNode(true));
      else hoverBox.replaceChildren();
    }
  }

  // links
  const href = item.dataset.href;
  if (href) {
    card.querySelectorAll('a[href]').forEach((a) => {
      if (a.closest('.pog-kleur')) return;
      a.setAttribute('href', href);
    });
  }

  // prijs (alleen kaarten met een enkele prijs)
  if (item.dataset.price) {
    const price = card.querySelector('product-price .price-item__group.price bdi, product-price .price bdi, .price-item__group.price bdi');
    if (price) price.textContent = item.dataset.price;
  }
}

const hoverCapable = window.matchMedia('(hover: hover)').matches;

document.addEventListener(
  'pointerenter',
  (event) => {
    if (!hoverCapable) return;
    const item = event.target instanceof Element ? event.target.closest('.pog-kleur--card .pog-kleur__item') : null;
    if (!item) return;
    selectCardColor(item);
  },
  true
);

// capture-fase: vóór Horizon's product-card.js (dat elke klik op de kaart naar de kaartlink stuurt)
document.addEventListener(
  'click',
  (event) => {
    const item = event.target instanceof Element ? event.target.closest('.pog-kleur--card .pog-kleur__item') : null;
    if (!item) return;
    event.stopPropagation();
    if (hoverCapable) return; // desktop: hover koos al, de <a> volgt zelf zijn href
    event.preventDefault(); // mobiel: alleen kleur kiezen; tik op de kaart gaat naar de gekozen kleur
    selectCardColor(item);
  },
  true
);

document.addEventListener('focusin', (event) => {
  const item = event.target instanceof Element ? event.target.closest('.pog-kleur--card .pog-kleur__item') : null;
  if (item) selectCardColor(item);
});
